// useProjectEvents — subscribe to the per-project WebSocket stream
// (plan §4.3, §M3.1, §M3.T.1).
//
// Wires live pipeline events into the Zustand pipelineStore so the
// PipelineStrip, PhaseBriefingPanel, and GateReviewPanel stay in sync
// without manual refetching. Automatically invalidates TanStack Query
// caches for briefings and gates when the relevant events arrive.

import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getClientId } from '@/lib/api'
import { phaseEventBus } from '@/lib/phaseEvents'
import { usePipelineStore } from '@/stores/pipelineStore'
import type { WSEvent } from '@/types'


const SUBPROTOCOL = 'ai_alm.v1'

export function useProjectEvents(
  projectId: string | null | undefined,
  runId: number | null | undefined,
  opts: { onEvent?: (event: WSEvent) => void } = {},
): void {
  // Stash the callback in a ref so a fresh `opts` object from the parent
  // render doesn't re-trigger the effect and tear down the WebSocket.
  const onEventRef = useRef(opts.onEvent)
  onEventRef.current = opts.onEvent

  const queryClient = useQueryClient()
  const setPhaseStatus = usePipelineStore((s) => s.setPhaseStatus)
  const setTokens = usePipelineStore((s) => s.setTokens)
  const setCurrentGate = usePipelineStore((s) => s.setCurrentGate)
  const setCurrentBriefing = usePipelineStore((s) => s.setCurrentBriefing)
  const setProjectRun = usePipelineStore((s) => s.setProjectRun)
  const agentStarted = usePipelineStore((s) => s.agentStarted)
  const agentCompleted = usePipelineStore((s) => s.agentCompleted)
  const setRqs = usePipelineStore((s) => s.setRqs)


  useEffect(() => {
    if (!projectId || !runId) return
    setProjectRun(projectId, runId)

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    // Use the `/ws/projects/...` alias on the backend so the Vite dev
    // server's `/ws` proxy entry forwards the upgrade. The bare
    // `/projects/{id}/events` path collides with the SPA's own routing
    // on port 3000 and produces the
    //   "WebSocket is closed before the connection is established"
    // error (the dev server returns the SPA HTML instead of proxying
    // the upgrade).
    // Multi-user (Phase 6 / R5) — append the per-browser client_id as a
    // query param so the server logs ws connect/disconnect with the same
    // breadcrumb the HTTP middleware emits via the ``X-Client-Id`` header.
    const clientId = encodeURIComponent(getClientId())
    const url =
      `${protocol}//${window.location.host}/ws/projects/${projectId}/events` +
      `?client_id=${clientId}`


    let ws: WebSocket | null = null
    let heartbeat: ReturnType<typeof setInterval> | null = null
    let cancelled = false
    let reconnectDelay = 1_000

    const connect = () => {
      if (cancelled) return
      const socket = new WebSocket(url, [SUBPROTOCOL])
      ws = socket

      socket.onopen = () => {
        // If the effect was torn down while the socket was still
        // CONNECTING (this is the normal path under React.StrictMode
        // in dev — mount → cleanup → remount), close the socket
        // *now* that it's actually OPEN. Calling .close() during
        // CONNECTING is what produced the misleading
        //   "WebSocket is closed before the connection is established"
        // warning in the browser console — closing in the OPEN state
        // is the documented well-formed teardown.
        if (cancelled) {
          socket.close(1000, 'effect-cancelled')
          return
        }
        reconnectDelay = 1_000
        if (heartbeat) clearInterval(heartbeat)
        heartbeat = setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'ping' }))
          }
        }, 30_000)
      }

      socket.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data) as WSEvent
          handleEvent(msg)
          // Fan out to the global phase-event bus so the
          // ``waitForPhaseCompletion`` helper in
          // ``lib/phaseEvents`` can resolve the mutation that
          // posted ``/phaseN?async=true``. The bus runs after the
          // local store-mutation handler so the pipelineStore is
          // already up-to-date by the time the mutation's onSuccess
          // refetches the artifact index.
          phaseEventBus.emit(msg)
          onEventRef.current?.(msg)
        } catch {
          // Ignore malformed payloads.
        }
      }


      socket.onclose = () => {
        if (heartbeat) clearInterval(heartbeat)
        if (!cancelled) {
          // Exponential backoff, capped at 10 s.
          setTimeout(connect, reconnectDelay)
          reconnectDelay = Math.min(reconnectDelay * 2, 10_000)
        }
      }

      socket.onerror = () => {
        // onclose handles reconnection; just swallow to avoid uncaught events.
      }
    }

    const handleEvent = (msg: WSEvent) => {
      switch (msg.event) {
        case 'phase_started': {
          const phase = msg.phase as 1 | 2 | 3 | 4 | 5
          if (phase) setPhaseStatus(phase, 'running')
          break
        }
        case 'phase_completed': {
          const phase = msg.phase as 1 | 2 | 3 | 4 | 5
          if (phase) setPhaseStatus(phase, 'done')
          queryClient.invalidateQueries({
            queryKey: ['project', msg.project_id],
          })
          break
        }
        case 'briefing_ready': {
          const phase = msg.phase as 1 | 2 | 3 | 4 | 5
          if (phase) {
            setCurrentBriefing(phase)
            setPhaseStatus(phase, 'briefing')
            queryClient.invalidateQueries({
              queryKey: ['briefing', msg.project_id, msg.run_id, phase],
            })
          }
          break
        }
        case 'gate_ready': {
          const gate = (msg.payload?.gate_number as 1 | 2 | 3 | 4 | 5) ?? null
          if (gate) {
            setCurrentGate(gate)
            setPhaseStatus(gate, 'gate')
            queryClient.invalidateQueries({
              queryKey: ['gate', msg.project_id, msg.run_id, gate],
            })
          }
          break
        }
        case 'budget_warning':
        case 'budget_halt': {
          const used = Number(msg.payload?.tokens_used ?? 0)
          const max = Number(msg.payload?.max_tokens ?? 500_000)
          setTokens(used, max)
          break
        }
        case 'rewind_initiated': {
          // Project list + project detail both need to refresh to show the
          // incremented rewind_count and new current_run.
          queryClient.invalidateQueries({ queryKey: ['projects'] })
          queryClient.invalidateQueries({
            queryKey: ['project', msg.project_id],
          })
          break
        }
        case 'artifact_written': {
          const phase = msg.phase as 1 | 2 | 3 | 4 | 5
          if (phase) setPhaseStatus(phase, 'done')
          break
        }
        case 'agent_started': {
          if (msg.agent) agentStarted(msg.agent)
          break
        }
        case 'agent_completed': {
          if (msg.agent) {
            const payload = msg.payload ?? {}
            agentCompleted(msg.agent, {
              inputTokens: Number(payload.input_tokens ?? 0),
              outputTokens: Number(payload.output_tokens ?? 0),
              files: Array.isArray(payload.files)
                ? (payload.files as string[])
                : [],
            })
          }
          break
        }
        case 'rqs_updated': {
          const payload = msg.payload ?? {}
          const score = (payload.score ?? {}) as Record<string, unknown>
          const breakdown = (score.breakdown ?? {}) as Record<string, number>
          setRqs({
            overall: Number(score.overall ?? 0),
            breakdown: {
              ears_conformance: Number(breakdown.ears_conformance ?? 0),
              incose_40: Number(breakdown.incose_40 ?? 0),
              traceability: Number(breakdown.traceability ?? 0),
              provenance: Number(breakdown.provenance ?? 0),
            },
            audience:
              typeof score.audience === 'string'
                ? (score.audience as string)
                : undefined,
            rewriteCount: Number(payload.rewrite_count ?? 0),
          })
          break
        }
        default:
          break
      }
    }


    connect()

    return () => {
      cancelled = true
      if (heartbeat) clearInterval(heartbeat)
      // Only close cleanly when the socket is actually OPEN. If it's
      // still CONNECTING (the StrictMode double-invoke path) the
      // `cancelled` flag inside `onopen` will close it once it
      // transitions to OPEN — closing here would emit the
      //   "WebSocket is closed before the connection is established"
      // warning. CLOSING / CLOSED states need no action.
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close(1000, 'effect-cleanup')
      }
    }
  }, [
    projectId,
    runId,
    queryClient,
    setPhaseStatus,
    setTokens,
    setCurrentGate,
    setCurrentBriefing,
    setProjectRun,
    agentStarted,
    agentCompleted,
    setRqs,
  ])
}

